import { CheckIcon, SendIcon } from 'lucide-react';

import { useEffect, useRef } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import {
  COMMENT_LIMITS,
  type CommentResponseDto,
  DELETED_COMMENT_SENTINEL,
} from '@splitfiesta/shared';
import { Avatar, Button } from '@splitfiesta/ui';

import { SupporterBadge } from '@/components/pro/SupporterBadge';
import { LinkButton } from '@/components/ui/LinkButton';
import { RelativeTime } from '@/components/ui/RelativeTime';

import { useCommentManagement } from '@/hooks/useCommentManagement';
import { useReportContent } from '@/hooks/useReportContent';

import { resolveUserName } from '@/lib/displayName';
import {
  bgColorClasses,
  borderClasses,
  focusRingClasses,
  textColorClasses,
} from '@/lib/styles/common';

// Time windows matching backend constants
const COMMENT_EDIT_WINDOW_MINUTES = 30;
const COMMENT_DELETE_WINDOW_HOURS = 24;

// Within this many characters of the cap, color the live counter amber
// as a gentle "running out of room" warning (it turns red at the cap).
const COMMENT_COUNTER_THRESHOLD = 25;

/**
 * Check if a comment is within the edit window (30 minutes)
 */
function isWithinEditWindow(createdAt: string): boolean {
  const commentAge = Date.now() - new Date(createdAt).getTime();
  const editWindowMs = COMMENT_EDIT_WINDOW_MINUTES * 60 * 1000;
  return commentAge <= editWindowMs;
}

/**
 * Check if a comment is within the delete window (24 hours)
 */
function isWithinDeleteWindow(createdAt: string): boolean {
  const commentAge = Date.now() - new Date(createdAt).getTime();
  const deleteWindowMs = COMMENT_DELETE_WINDOW_HOURS * 60 * 60 * 1000;
  return commentAge <= deleteWindowMs;
}

interface CommentSectionProps {
  comments: CommentResponseDto[];
  currentUserId?: string;
  currentUserName?: string;
  currentUserProfileImage?: string;
  onSubmitComment: (text: string) => Promise<void>;
  onEditComment: (commentId: string, text: string) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
  placeholder?: string;
  hasMoreComments?: boolean;
  onLoadMoreComments?: () => void | Promise<void>;
  loadingMoreComments?: boolean;
  /** This surface shows Supporter badges; the badge checks the `pro` flag. */
  showSupporterBadges?: boolean;
}

export function CommentSection({
  comments,
  currentUserId,
  currentUserName = 'Unknown',
  currentUserProfileImage,
  onSubmitComment,
  onEditComment,
  onDeleteComment,
  placeholder,
  hasMoreComments = false,
  onLoadMoreComments,
  loadingMoreComments = false,
  showSupporterBadges = false,
}: CommentSectionProps) {
  const { t } = useTranslation('social');
  const reportContent = useReportContent();

  // Load groups namespace for timeAgo translations
  useNamespace('groups');

  // Use our comment management hook
  const {
    commentText,
    setCommentText,
    handleSubmitComment,
    submitComment,
    isSubmitting,
    startEditing,
    cancelEditing,
    handleEditSubmit,
    isEditingComment,
    getEditText,
    setEditText,
    handleDeleteComment,
    isDeletingComment,
    handleKeyDown,
  } = useCommentManagement({
    onSubmitComment,
    onEditComment,
    onDeleteComment,
  });

  const composerRef = useRef<HTMLTextAreaElement>(null);

  // After a send clears the draft, the textarea keeps the inline height
  // its auto-resize set while typing — collapse it back to one line.
  useEffect(() => {
    if (commentText === '' && composerRef.current) {
      composerRef.current.style.height = 'auto';
    }
  }, [commentText]);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    setCommentText(textarea.value);

    // Auto-resize textarea
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  const handleStartEdit = (commentId: string, currentText: string) => {
    startEditing(commentId, currentText);

    setTimeout(() => {
      const textarea = document.querySelector(
        `textarea[data-comment-id="${commentId}"]`
      ) as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(
          textarea.value.length,
          textarea.value.length
        );

        const singleLineHeight = 34;
        textarea.style.height = `${singleLineHeight}px`;

        if (textarea.scrollHeight > singleLineHeight) {
          textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
        }
      }
    }, 0);
  };

  const handleSaveEdit = async (commentId: string) => {
    const newText = getEditText(commentId).trim();
    if (!newText) return;

    const originalComment = comments.find(c => c.id === commentId);
    if (originalComment && newText === originalComment.comment.trim()) {
      cancelEditing(commentId);
      return;
    }

    await handleEditSubmit(commentId);
  };

  const handleDeleteWithConfirm = async (commentId: string) => {
    if (!confirm(t('comments.deleteComment'))) return;
    await handleDeleteComment(commentId);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, commentId: string) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit(commentId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing(commentId);
    }
  };

  const handleEditTextareaChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    commentId: string
  ) => {
    const textarea = e.target;
    setEditText(commentId, textarea.value);

    const hasLineBreaks = textarea.value.includes('\n');
    if (hasLineBreaks) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    } else {
      textarea.style.height = '34px';
    }
  };

  return (
    <div data-testid="comment-section">
      {/* Comments Display */}
      {comments.length > 0 && (
        <div className="space-y-3 mb-3">
          {comments.map(comment => (
            <div key={comment.id} className="flex items-start gap-3">
              <Avatar
                name={comment.name}
                src={comment.profileImage}
                size="sm"
                className="shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="bg-[#F6F5F3] dark:bg-gray-700 rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                      {resolveUserName(comment.name, t)}
                      {showSupporterBadges && comment.isSupporter && (
                        <SupporterBadge className="ml-1" />
                      )}
                    </p>
                    {comment.userId !== currentUserId && (
                      <Button
                        data-testid="comment-report-button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          reportContent({
                            entityType: 'comment',
                            entityId: comment.id,
                          })
                        }
                        className="text-xs text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:underline p-0 h-auto"
                      >
                        {t('common:report.action')}
                      </Button>
                    )}
                    {comment.userId === currentUserId && (
                      <div className="flex items-center gap-3">
                        {comment.comment !== DELETED_COMMENT_SENTINEL &&
                          isWithinEditWindow(comment.createdAt) && (
                            <Button
                              data-testid="comment-edit-button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleStartEdit(comment.id, comment.comment)
                              }
                              className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:underline p-0 h-auto"
                            >
                              {t('common:actions.edit')}
                            </Button>
                          )}
                        {isWithinDeleteWindow(comment.createdAt) && (
                          <Button
                            data-testid="comment-delete-button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteWithConfirm(comment.id)}
                            disabled={isDeletingComment(comment.id)}
                            className="text-xs text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:underline p-0 h-auto"
                          >
                            {isDeletingComment(comment.id)
                              ? t('common:actions.deleting')
                              : t('common:actions.delete')}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  {isEditingComment(comment.id) ? (
                    <div
                      className="flex items-end gap-2"
                      data-edit-container={comment.id}
                    >
                      <textarea
                        data-testid="comment-edit-input"
                        data-comment-id={comment.id}
                        maxLength={COMMENT_LIMITS.MAX_LENGTH}
                        value={getEditText(comment.id)}
                        onChange={e => handleEditTextareaChange(e, comment.id)}
                        onKeyDown={e => handleEditKeyDown(e, comment.id)}
                        className={`flex-1 px-3 py-1.5 text-sm ${textColorClasses.primary} ${bgColorClasses.input} border ${borderClasses.light} rounded-lg ${focusRingClasses} resize-none overflow-hidden`}
                        style={{
                          minHeight: '34px',
                          maxHeight: '120px',
                        }}
                        placeholder={t('comments.editComment')}
                      />
                      {getEditText(comment.id) !== comment.comment &&
                        getEditText(comment.id).trim() &&
                        getEditText(comment.id).trim() !==
                          comment.comment.trim() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSaveEdit(comment.id)}
                            className="p-1.5 text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-full"
                            title={t('common:actions.saveChanges')}
                          >
                            <CheckIcon className="w-4 h-4" />
                          </Button>
                        )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                      {comment.comment === DELETED_COMMENT_SENTINEL ? (
                        <span className="italic text-gray-500 dark:text-gray-400">
                          {t('common:comments.deleted')}
                        </span>
                      ) : (
                        comment.comment
                      )}
                    </p>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <RelativeTime date={comment.createdAt} />
                  {/* Anonymization sets isEdited — never claim the author
                      edited a comment the system wiped */}
                  {comment.isEdited &&
                    comment.comment !== DELETED_COMMENT_SENTINEL && (
                      <span className="ml-1">• {t('comments.edited')}</span>
                    )}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Show all comments button */}
      {hasMoreComments && onLoadMoreComments && (
        <div className="mb-4 flex justify-center">
          <LinkButton
            onClick={onLoadMoreComments}
            disabled={loadingMoreComments}
          >
            {loadingMoreComments
              ? t('common:actions.loading')
              : t('comments.showAllComments')}
          </LinkButton>
        </div>
      )}

      {/* Comment Input */}
      <div className="flex items-start gap-2">
        <Avatar
          name={currentUserName}
          src={currentUserProfileImage}
          size="sm"
          className="shrink-0"
        />
        <div className="flex-1">
          <div className="flex items-end gap-2">
            <textarea
              ref={composerRef}
              data-testid="comment-input"
              placeholder={placeholder || t('comments.addComment')}
              value={commentText}
              onChange={handleTextareaChange}
              onKeyDown={e => handleKeyDown(e, submitComment)}
              disabled={isSubmitting}
              maxLength={COMMENT_LIMITS.MAX_LENGTH}
              rows={1}
              className={`flex-1 px-3 py-1.5 text-sm ${textColorClasses.primary} ${bgColorClasses.input} border ${borderClasses.light} rounded-lg ${focusRingClasses} disabled:opacity-50 resize-none overflow-hidden`}
              style={{
                minHeight: '34px',
                maxHeight: '120px',
              }}
            />
            {commentText.trim() && (
              <Button
                data-testid="comment-submit-button"
                variant="ghost"
                size="sm"
                onClick={handleSubmitComment}
                disabled={isSubmitting}
                className="p-1.5 text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-full"
                title={t('comments.sendComment')}
              >
                <SendIcon className="w-4 h-4" />
              </Button>
            )}
          </div>
          {commentText.length > 0 && (
            <p
              data-testid="comment-char-counter"
              className={`mt-1 pr-1 text-right text-xs ${
                commentText.length >= COMMENT_LIMITS.MAX_LENGTH
                  ? 'text-red-500'
                  : commentText.length >=
                      COMMENT_LIMITS.MAX_LENGTH - COMMENT_COUNTER_THRESHOLD
                    ? 'text-amber-600 dark:text-amber-500'
                    : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {commentText.length}/{COMMENT_LIMITS.MAX_LENGTH}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
